const DISPLAY_CAP = 20;
const DEFAULT_SAFETY_LIMIT = 5000;

const sanitiseClues = (clues) => clues.filter((n) => Number.isInteger(n) && n > 0);

export const computeMinSpan = (clues) => {
    const blocks = sanitiseClues(clues);
    if (blocks.length === 0) return 0;
    return blocks.reduce((a, b) => a + b, 0) + (blocks.length - 1);
};

export const computeSlack = (rowLength, clues) => {
    const minSpan = computeMinSpan(clues);
    return { minSpan, slack: rowLength - minSpan, isImpossible: rowLength - minSpan < 0 };
};

export const computeForcedOverlapSegments = (clues, slack) =>
    sanitiseClues(clues).map((length, blockIndex) => ({
        blockIndex,
        length,
        overlapLength: Math.max(0, length - slack)
    }));

const isCellAllowed = (cellStates, index, desiredPlacementValue) => {
    const userState = cellStates[index];
    if (userState === 'filled' && desiredPlacementValue !== 'filled') return false;
    if (userState === 'crossed' && desiredPlacementValue !== 'empty') return false;
    return true;
};

// Shared backtracking placer: tries each block's start position left-to-right,
// ascending, so the first result found by a depth-first walk is always the
// lexicographically-leftmost valid placement. `onPlacement` returning true
// stops the search early (used for leftmost-only lookups).
const searchPlacements = (rowLength, clues, cellStates, onPlacement, safetyLimit) => {
    const blocks = sanitiseClues(clues);
    let stopped = false;

    const tryFillRemainder = (current, fromIndex) => {
        const result = current.slice();
        for (let i = fromIndex; i < rowLength; i++) {
            if (!isCellAllowed(cellStates, i, 'empty')) return null;
            result[i] = 'empty';
        }
        return result;
    };

    const place = (blockIndex, pos, current) => {
        if (stopped) return;

        if (blockIndex === blocks.length) {
            const completed = tryFillRemainder(current, pos);
            if (completed && onPlacement(completed)) stopped = true;
            return;
        }

        const blockLen = blocks[blockIndex];
        const remaining = blocks.slice(blockIndex + 1);
        const minRemaining = remaining.reduce((a, b) => a + b, 0) + remaining.length;
        const maxStart = rowLength - blockLen - minRemaining;
        const loopStart = blockIndex === 0 ? pos : pos + 1;

        for (let start = loopStart; start <= maxStart; start++) {
            if (stopped) return;

            let gapOk = true;
            for (let i = pos; i < start; i++) {
                if (!isCellAllowed(cellStates, i, 'empty')) { gapOk = false; break; }
            }
            if (!gapOk) continue;

            let blockOk = true;
            for (let i = start; i < start + blockLen; i++) {
                if (!isCellAllowed(cellStates, i, 'filled')) { blockOk = false; break; }
            }
            if (!blockOk) continue;

            const next = current.slice();
            for (let i = pos; i < start; i++) next[i] = 'empty';
            for (let i = start; i < start + blockLen; i++) next[i] = 'filled';

            place(blockIndex + 1, start + blockLen, next);
        }
    };

    if (blocks.length === 0) {
        const empty = tryFillRemainder(Array(rowLength).fill('empty'), 0);
        if (empty) onPlacement(empty);
        return;
    }

    place(0, 0, Array(rowLength).fill('empty'));
};

export const generateValidPlacements = (rowLength, clues, cellStates, safetyLimit = DEFAULT_SAFETY_LIMIT) => {
    const placements = [];
    let hitSafetyLimit = false;

    searchPlacements(rowLength, clues, cellStates, (placement) => {
        placements.push(placement);
        if (placements.length >= safetyLimit) {
            hitSafetyLimit = true;
            return true;
        }
        return false;
    });

    return { placements, hitSafetyLimit };
};

export const getLeftmostPlacement = (rowLength, clues, cellStates) => {
    let found = null;
    searchPlacements(rowLength, clues, cellStates, (placement) => {
        found = placement;
        return true;
    });
    return found;
};

// Rightmost placement = leftmost placement of the mirrored problem (reversed
// row and reversed clue order), reversed back — reuses the single search path
// instead of a second, differently-ordered traversal.
export const getRightmostPlacement = (rowLength, clues, cellStates) => {
    const mirroredClues = [...sanitiseClues(clues)].reverse();
    const mirroredCellStates = [...cellStates].reverse();
    const mirrored = getLeftmostPlacement(rowLength, mirroredClues, mirroredCellStates);
    return mirrored ? [...mirrored].reverse() : null;
};

// Reads off each block's start position from a concrete valid placement, in
// clue order — used to find where a specific block sits within the leftmost
// or rightmost arrangement.
const getBlockStarts = (placement, clues) => {
    const blocks = sanitiseClues(clues);
    const starts = [];
    let cursor = 0;
    for (const length of blocks) {
        while (placement[cursor] !== 'filled') cursor++;
        starts.push(cursor);
        cursor += length;
    }
    return starts;
};

// The sound version of the "overlap" trick: a cell is only guaranteed filled
// if it falls within block i's overlap window, where block i's own leftmost
// start (from the fully-left-justified placement) and rightmost start (from
// the fully-right-justified placement) overlap. Naively intersecting the two
// full placement arrays cell-by-cell is NOT sound — a cell can be filled by
// a different block in each extreme placement and still be empty in some
// valid arrangement in between. This only ever asserts 'filled', never
// 'crossed', since the empty-cell case doesn't have the same per-block
// attribution and isn't provably safe from just these two placements.
export const computeOverlapCells = (leftmost, rightmost, clues, rowLength) => {
    if (!leftmost || !rightmost) return null;

    const blocks = sanitiseClues(clues);
    const leftStarts = getBlockStarts(leftmost, clues);
    const rightStarts = getBlockStarts(rightmost, clues);

    const cells = Array(rowLength).fill('ambiguous');
    blocks.forEach((length, i) => {
        const windowStart = rightStarts[i];
        const windowEnd = leftStarts[i] + length - 1;
        for (let cell = windowStart; cell <= windowEnd; cell++) {
            cells[cell] = 'filled';
        }
    });
    return cells;
};

export const computeGuaranteedCells = (placements, rowLength) => {
    if (placements.length === 0) return Array(rowLength).fill('ambiguous');

    const filledCounts = Array(rowLength).fill(0);
    placements.forEach((placement) => {
        for (let i = 0; i < rowLength; i++) {
            if (placement[i] === 'filled') filledCounts[i]++;
        }
    });

    const total = placements.length;
    return filledCounts.map((count) => {
        if (count === total) return 'filled';
        if (count === 0) return 'crossed';
        return 'ambiguous';
    });
};

export const solveRow = (rowLength, clues, cellStates) => {
    const blocks = sanitiseClues(clues);
    const { minSpan, slack, isImpossible } = computeSlack(rowLength, blocks);

    if (isImpossible) {
        return {
            isImpossible, contradiction: false, enumerationOverflow: false,
            minSpan, slack, forcedOverlapSegments: [],
            leftmost: null, rightmost: null, overlapCells: null,
            placements: [], placementsTruncated: false, totalValidCount: 0,
            guaranteedCells: Array(rowLength).fill('ambiguous')
        };
    }

    const forcedOverlapSegments = computeForcedOverlapSegments(blocks, slack);
    const leftmost = getLeftmostPlacement(rowLength, blocks, cellStates);
    const rightmost = getRightmostPlacement(rowLength, blocks, cellStates);
    const overlapCells = computeOverlapCells(leftmost, rightmost, blocks, rowLength);

    const { placements, hitSafetyLimit } = generateValidPlacements(rowLength, blocks, cellStates);
    const contradiction = placements.length === 0;

    let guaranteedCells;
    if (contradiction) {
        guaranteedCells = Array(rowLength).fill('ambiguous');
    } else if (hitSafetyLimit) {
        guaranteedCells = overlapCells;
    } else {
        guaranteedCells = computeGuaranteedCells(placements, rowLength);
    }

    return {
        isImpossible: false,
        contradiction,
        enumerationOverflow: hitSafetyLimit,
        minSpan, slack, forcedOverlapSegments,
        leftmost, rightmost, overlapCells,
        placements,
        placementsTruncated: placements.length > DISPLAY_CAP,
        totalValidCount: placements.length,
        guaranteedCells
    };
};

export { DISPLAY_CAP };
